import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getApiUrl } from '../config';
import { useNotification, Notification } from './Notification';
import { theme, alpha } from '../theme';
import { DateInput, TimeInput } from './DateTimeInputs';
import { logger } from '../utils/logger';
import { useResponsive } from '../hooks/useResponsive';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import { AppHeader } from './AppHeader';
import { SpecificHelpModal } from './Help';
import { useGroups } from '../hooks/useGroups';
import * as analytics from '../lib/analytics';
import { TeamColorPicker } from './ViewMatch/TeamColorPicker';

type MatchGenderType = 'MIXED' | 'MALE' | 'FEMALE';

const CreateMatch: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const groupId = searchParams.get('groupId') || '';
  const groupName = searchParams.get('groupName') || '';
  const [formData, setFormData] = useState({
    location: '', // nombre de la cancha
    mapsUrl: '', // link de Google Maps
    time: '',
    date: '',
    maxPlayers: 5, // por defecto 5 jugadores por equipo
    price: '',
    team1Name: 'Equipo 1',
    team2Name: 'Equipo 2',
    team1Color: '#4CAF50',
    team2Color: '#2196F3'
  });
  const [loading, setLoading] = useState(false);
  const [dataLoadedFromLanding, setDataLoadedFromLanding] = useState(false);

  // Recurring match state (solo visible cuando hay groupId)
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState<'WEEKLY' | 'BIWEEKLY' | 'TRIWEEKLY' | 'MONTHLY'>('WEEKLY');
  const [recurringDayOfWeek, setRecurringDayOfWeek] = useState<number | null>(null);
  const [recurringName, setRecurringName] = useState('');
  const [previousLocations, setPreviousLocations] = useState<Array<{id: string, location: string, mapsUrl: string}>>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<Array<{id: string, location: string, mapsUrl: string}>>([]);
  const [showHelp, setShowHelp] = useState(false);
  const { notification, showNotification, hideNotification } = useNotification();
  const { isMobile } = useResponsive();
  const flags = useFeatureFlags();
  const publicMatchDiscover = Boolean(flags.PUBLIC_MATCH_DISCOVER);
  const { groups: userGroups } = useGroups();

  // Public match filters (paridad con mobile CreateMatchScreen)
  const [isPublicMatch, setIsPublicMatch] = useState(false);
  // Cancha "a definir": se crea el partido sin cancha y se define más adelante.
  const [venueTbd, setVenueTbd] = useState(false);
  const [matchGenderType, setMatchGenderType] = useState<MatchGenderType>('MIXED');
  const [skillLevelMinStr, setSkillLevelMinStr] = useState('');
  const [skillLevelMaxStr, setSkillLevelMaxStr] = useState('');

  // Admin groups for "Asociar a grupo" dropdown
  interface AdminGroup { id: string; name: string; }
  const [adminGroups, setAdminGroups] = useState<AdminGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>(groupId);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    fetch(getApiUrl('/api/users/me/admin-groups'), {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    })
      .then((r) => r.json())
      .then((j) => setAdminGroups(j.groups || []))
      .catch(() => setAdminGroups([]));
  }, []);

  // Cargar ubicaciones anteriores al montar el componente
  useEffect(() => {
    fetchRecentLocations();
    loadLandingFormData(); // Cargar datos del formulario de landing si existen
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Función para cargar datos del formulario de la landing page
  const loadLandingFormData = () => {
    try {
      const landingFormData = localStorage.getItem('landingFormData');
      if (landingFormData) {
        const parsedData = JSON.parse(landingFormData);
        
        // Mapear los datos de la landing page (o partido repetido) al formato de CreateMatch
        const isRepeat = !!(parsedData.team1Name || parsedData.team2Name);
        setFormData(prev => ({
          ...prev,
          location: parsedData.location || '',
          mapsUrl: parsedData.mapsUrl || '',
          time: parsedData.time || '',
          date: parsedData.date || '',
          maxPlayers: parsedData.maxPlayers || 5,
          price: parsedData.price || '',
          ...(parsedData.team1Name && { team1Name: parsedData.team1Name }),
          ...(parsedData.team2Name && { team2Name: parsedData.team2Name }),
        }));

        // Marcar que los datos se cargaron desde la landing
        setDataLoadedFromLanding(true);

        // Mostrar notificación
        showNotification(
          isRepeat
            ? 'Datos del partido anterior cargados. Elegí fecha y hora.'
            : 'Datos del formulario de landing cargados automáticamente',
          'success'
        );
        
        // Limpiar el localStorage después de cargar los datos
        localStorage.removeItem('landingFormData');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error loading landing form data:', error);
      // Si hay error, limpiar el localStorage para evitar problemas futuros
      localStorage.removeItem('landingFormData');
    }
  };

  // Función para obtener ubicaciones recientes desde la base de datos
  const fetchRecentLocations = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return;
      }
      const response = await fetch(getApiUrl('/api/matches/recent-locations'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPreviousLocations(data.recentLocations);
      } else {
        const errorData = await response.json();
        // eslint-disable-next-line no-console
        console.error('Error response:', errorData);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching recent locations:', error);
    }
  };

  // Función para obtener la configuración del último partido
  const fetchLastMatchSettings = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return;
      }
      const response = await fetch(getApiUrl('/api/matches/last-match-settings'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Aplicar la configuración del último partido
        const lastMatch = data.lastMatch;
        setFormData({
          ...formData,
          date: lastMatch.date,
          time: lastMatch.time,
          location: lastMatch.location,
          mapsUrl: lastMatch.mapsUrl,
          maxPlayers: lastMatch.maxPlayers,
          price: lastMatch.price || ''
        });
        
        showNotification('Configuración del último partido aplicada', 'success');
      } else {
        const errorData = await response.json();
        // eslint-disable-next-line no-console
        console.error('Error response:', errorData);
        showNotification(errorData.error || 'No se encontraron partidos anteriores', 'info');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching last match settings:', error);
      showNotification('Error al obtener la configuración del último partido', 'error');
    }
  };

  // Función para filtrar sugerencias
  const filterSuggestions = (input: string) => {
    if (!input.trim()) {
      // Si no hay texto, mostrar todas las ubicaciones recientes
      setFilteredSuggestions(previousLocations);
      setShowSuggestions(previousLocations.length > 0);
      return;
    }
    
    const filtered = previousLocations.filter(location => 
      location.location.toLowerCase().includes(input.toLowerCase())
    );
    setFilteredSuggestions(filtered);
    setShowSuggestions(filtered.length > 0);
  };

  // Función para seleccionar una sugerencia
  const selectSuggestion = (location: {id: string, location: string, mapsUrl: string}) => {
    setFormData({
      ...formData, 
      location: location.location,
      mapsUrl: location.mapsUrl
    });
    setShowSuggestions(false);
  };

  // Maps URL es opcional: si el user pega algo, mostramos un warning suave debajo
  // del input pero NO bloqueamos el submit. Si está vacío, no mostramos nada.
  const mapsUrlIsValid = (() => {
    if (!formData.mapsUrl.trim()) return true;
    try {
      new URL(formData.mapsUrl);
      return true;
    } catch {
      return false;
    }
  })();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Flujo recurrente
    if (isRecurring && groupId) {
      if (recurringDayOfWeek === null) {
        showNotification('Seleccioná el día de la semana del partido fijo.', 'error');
        return;
      }
      if (!formData.time) {
        showNotification('Ingresá el horario del partido fijo.', 'error');
        return;
      }
      const parsedPrice = parseFloat(formData.price);
      if (isNaN(parsedPrice) || parsedPrice < 0) {
        showNotification('El precio ingresado no es válido.', 'error');
        return;
      }
      setLoading(true);
      try {
        const token = localStorage.getItem('authToken');
        const body = {
          name: recurringName.trim() || null,
          location: formData.location,
          mapsUrl: formData.mapsUrl,
          time: formData.time,
          dayOfWeek: recurringDayOfWeek,
          frequency: recurringFrequency,
          price: formData.price,
          maxPlayers: formData.maxPlayers * 2,
          team1Name: formData.team1Name,
          team2Name: formData.team2Name,
          team1Color: formData.team1Color,
          team2Color: formData.team2Color,
        };
        const response = await fetch(getApiUrl(`/api/groups/${groupId}/recurring-matches`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(body),
        });
        if (response.ok) {
          showNotification('¡Partido fijo creado! Se generará automáticamente cada semana.', 'success');
          setTimeout(() => navigate(`/groups/${groupId}`), 1000);
        } else {
          const errorData = await response.json();
          showNotification(errorData.error || 'Error al crear el partido fijo', 'error');
        }
      } catch (error) {
        logger.error('Error creating recurring match:', error);
        showNotification('Error de conexión. Intenta nuevamente.', 'error');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Flujo normal — validaciones de fecha
    if (!formData.date || !formData.time) {
      showNotification('Por favor, selecciona una fecha y hora para el partido.', 'error');
      return;
    }

    // Permitir cargar un partido que ya arrancó (te olvidaste de cargarlo y lo
    // estás por jugar): se aceptan fechas de hasta 6h en el pasado.
    const selectedDate = new Date(formData.date + 'T' + formData.time);
    const PAST_GRACE_MS = 6 * 60 * 60 * 1000;
    if (selectedDate.getTime() < Date.now() - PAST_GRACE_MS) {
      showNotification('La fecha y hora no puede ser más de 6 horas en el pasado.', 'error');
      return;
    }

    const parsedPrice = parseFloat(formData.price);
    if (isNaN(parsedPrice) || parsedPrice < 0 || parsedPrice > 9999999) {
      showNotification('El precio ingresado no es válido.', 'error');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('authToken');

      let skillMin: number | undefined;
      let skillMax: number | undefined;
      if (publicMatchDiscover && isPublicMatch) {
        const minN = parseInt(skillLevelMinStr, 10);
        if (!isNaN(minN) && minN >= 1 && minN <= 5) skillMin = minN;
        const maxN = parseInt(skillLevelMaxStr, 10);
        if (!isNaN(maxN) && maxN >= 1 && maxN <= 5) skillMax = maxN;
        if (skillMin !== undefined && skillMax !== undefined && skillMin > skillMax) {
          showNotification('El nivel mínimo no puede ser mayor al máximo.', 'error');
          setLoading(false);
          return;
        }
      }

      const matchData = {
        ...formData,
        // Sin cancha: el server completa `location` y lo deja privado (no se
        // puede descubrir en el mapa sin coordenadas).
        ...(venueTbd ? { venueTbd: true, location: '', mapsUrl: '' } : {}),
        ...(selectedGroupId ? { groupId: selectedGroupId } : {}),
        ...(publicMatchDiscover && !venueTbd ? {
          isPublic: isPublicMatch,
          ...(isPublicMatch ? {
            matchGenderType,
            skillLevelMin: skillMin,
            skillLevelMax: skillMax,
          } : {}),
        } : {}),
      };

      const response = await fetch(getApiUrl('/api/matches'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(matchData),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem(`match_${data.match.id}_creator`, data.creatorId);
        showNotification(data.message || '¡Partido creado exitosamente! Te has unido automáticamente como jugador.', 'success');
        const showGroupNudge = !selectedGroupId && userGroups.length === 0 && adminGroups.length === 0;
        if (showGroupNudge) {
          analytics.track('group_post_match_nudge_shown', { platform: 'web' });
          showNotification(
            '¿Se repite cada semana? Creá un grupo y la próxima se arma sola',
            'info',
            {
              durationMs: 8000,
              action: {
                label: 'Crear grupo',
                onClick: () => {
                  analytics.track('group_post_match_nudge_clicked', { platform: 'web' });
                  navigate('/groups/create');
                },
              },
            }
          );
        }
        setTimeout(() => {
          if (selectedGroupId) navigate(`/groups/${selectedGroupId}`);
          else navigate(`/match/${data.match.id}`);
        }, showGroupNudge ? 8000 : 1000);
      } else {
        const errorData = await response.json();
        showNotification(errorData.error || 'Error al crear el partido', 'error');
      }
    } catch (error) {
      logger.error('Error creating match:', error);
      showNotification('Error de conexión. Intenta nuevamente.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      color: theme.colors.text,
      width: '100%',
      boxSizing: 'border-box'
    }}>
      <AppHeader />
      
      <div style={{
        padding: isMobile ? '12px' : theme.spacing.lg,
        marginTop: isMobile ? 60 : 0
      }}>
        <Notification
          message={notification.message}
          type={notification.type}
          isVisible={notification.isVisible}
          onClose={hideNotification}
          action={notification.action}
          durationMs={notification.durationMs}
        />
        
        <div style={{
          backgroundColor: theme.colors.surface,
          borderRadius: theme.borderRadius.card,
          padding: isMobile ? '16px' : theme.spacing.lg,
          boxShadow: theme.colors.shadow,
          border: `2px solid ${theme.colors.primary}`,
          maxWidth: isMobile ? '100%' : '800px',
          margin: isMobile ? '0' : '0 auto',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <h2 style={{
            margin: '0 0 24px 0',
            color: theme.colors.primary,
            fontFamily: theme.fonts.heading,
            fontSize: isMobile ? '22px' : theme.fonts.sizeTitle,
            letterSpacing: theme.fonts.letterSpacingWide,
            textTransform: 'uppercase',
            textAlign: 'center'
          }}>Crear Nuevo Partido</h2>

          {/* Group chip */}
          {groupId && groupName && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 12, background: `${alpha('primary', '18')}`, border: `1px solid ${alpha('primary', '50')}`, marginBottom: 20, fontSize: 14, fontWeight: 600, color: theme.colors.primary }}>
              <span>⚽</span>
              <span style={{ flex: 1 }}>Partido para {groupName}</span>
              <span style={{ fontSize: 12 }}>🔒</span>
            </div>
          )}

          {/* Indicador de datos cargados desde landing page */}
          {dataLoadedFromLanding && (
            <div style={{
              backgroundColor: `${alpha('success', '20')}`,
              border: `1px solid ${theme.colors.success}`,
              borderRadius: theme.borderRadius.input,
              padding: '12px 16px',
              marginBottom: '20px',
              textAlign: 'center',
              color: theme.colors.success,
              fontSize: '14px',
              fontWeight: 500
            }}>
              ✅ Datos cargados automáticamente desde la landing page
            </div>
          )}
          
          {/* Botón para usar configuración del último partido */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: isMobile ? '16px' : '24px'
          }}>
            <button
              type="button"
              onClick={fetchLastMatchSettings}
              style={{
                backgroundColor: theme.colors.secondary,
                color: theme.colors.text,
                border: `2px solid ${theme.colors.secondary}`,
                borderRadius: theme.borderRadius.button,
                padding: isMobile ? '10px 16px' : '12px 20px',
                fontSize: isMobile ? '14px' : theme.fonts.sizeBody,
                fontFamily: theme.fonts.body,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.primary;
                e.currentTarget.style.borderColor = theme.colors.primary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.secondary;
                e.currentTarget.style.borderColor = theme.colors.secondary;
              }}
            >
              <span style={{ fontSize: '16px' }}>🔄</span>
              Usar configuración del último partido
            </button>
          </div>
          
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '12px' : theme.spacing.md, width: '100%' }}>
            {/* Cancha a definir: armar el partido primero y cerrar la cancha después. */}
            <label
              htmlFor="create-match-venue-tbd"
              style={{
                display: 'flex', alignItems: 'center', gap: theme.spacing.sm,
                color: theme.colors.text, fontFamily: theme.fonts.body,
                fontSize: theme.fonts.sizeBody, cursor: 'pointer',
              }}
            >
              <input
                id="create-match-venue-tbd"
                data-testid="create-match-venue-tbd"
                type="checkbox"
                checked={venueTbd}
                onChange={(e) => setVenueTbd(e.target.checked)}
              />
              Todavía no sé la cancha, la defino después
            </label>

            <div style={{ display: venueTbd ? 'none' : 'flex', flexDirection: 'column', gap: theme.spacing.sm, position: 'relative' }}>
              <label htmlFor="create-match-location" style={{
                color: theme.colors.text,
                fontFamily: theme.fonts.body,
                fontWeight: 600,
                fontSize: theme.fonts.sizeBody
              }}>Nombre de la cancha:</label>
              <input
                id="create-match-location"
                type="text"
                value={formData.location}
                onChange={(e) => {
                  setFormData({...formData, location: e.target.value});
                  if (!e.target.value.trim()) {
                    // Si el input está vacío, mostrar todas las ubicaciones
                    setFilteredSuggestions(previousLocations);
                    setShowSuggestions(previousLocations.length > 0);
                  } else {
                    // Si hay texto, filtrar las sugerencias
                    filterSuggestions(e.target.value);
                  }
                }}
                onFocus={() => {
                  // Mostrar todas las ubicaciones recientes al hacer focus
                  setFilteredSuggestions(previousLocations);
                  setShowSuggestions(previousLocations.length > 0);
                }}
                onBlur={() => {
                  // Pequeño delay para permitir hacer clic en las sugerencias
                  setTimeout(() => setShowSuggestions(false), 200);
                }}
                placeholder="Ej: Cancha Sucre"
                required={!venueTbd}
                style={{
                  padding: isMobile ? '12px' : `${theme.spacing.sm} ${theme.spacing.md}`,
                  border: `2px solid ${theme.colors.border}`,
                  borderRadius: theme.borderRadius.input,
                  fontSize: isMobile ? '15px' : theme.fonts.sizeBody,
                  fontFamily: theme.fonts.body,
                  backgroundColor: theme.colors.background,
                  color: theme.colors.text,
                  transition: 'all 0.2s ease',
                  width: '100%'
                }}
              />
              
              {/* Sugerencias de ubicaciones anteriores */}
              {showSuggestions && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: theme.colors.surface,
                  border: `2px solid ${theme.colors.primary}`,
                  borderRadius: theme.borderRadius.input,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  zIndex: 1000,
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  {filteredSuggestions.length > 0 ? (
                    <>
                      {/* Título cuando se muestran todas las ubicaciones */}
                      {!formData.location.trim() && (
                        <div style={{
                          padding: '8px 16px',
                          backgroundColor: theme.colors.primary + '20',
                          borderBottom: `1px solid ${theme.colors.border}`,
                          color: theme.colors.text,
                          fontFamily: theme.fonts.body,
                          fontSize: '12px',
                          fontWeight: 600,
                          textAlign: 'center'
                        }}>
                          📍 Ubicaciones recientes ({filteredSuggestions.length})
                        </div>
                      )}
                      {filteredSuggestions.map((location, index) => (
                    <button
                      key={location.id}
                      type="button"
                      onClick={() => selectSuggestion(location)}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        borderBottom: index < filteredSuggestions.length - 1 ? `1px solid ${theme.colors.border}` : 'none',
                        color: theme.colors.text,
                        fontFamily: theme.fonts.body,
                        fontSize: theme.fonts.sizeBody,
                        transition: 'background-color 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        backgroundColor: 'transparent',
                        width: '100%',
                        border: 'none',
                        textAlign: 'left'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = theme.colors.primary + '20';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <span style={{ fontSize: '16px' }}>📍</span>
                      {location.location}
                    </button>
                  ))}
                    </>
                  ) : (
                    <div style={{
                      padding: '16px',
                      textAlign: 'center',
                      color: theme.colors.textSecondary,
                      fontFamily: theme.fonts.body,
                      fontSize: theme.fonts.sizeBody
                    }}>
                      📍 No tienes ubicaciones recientes
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div style={{ display: venueTbd ? 'none' : 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
              <label htmlFor="create-match-maps-url" style={{
                color: theme.colors.text,
                fontFamily: theme.fonts.body,
                fontWeight: 600,
                fontSize: theme.fonts.sizeBody
              }}>Link de Google Maps (opcional):</label>
              <input
                id="create-match-maps-url"
                type="text"
                value={formData.mapsUrl}
                onChange={(e) => setFormData({...formData, mapsUrl: e.target.value})}
                placeholder="Pega aquí el link de Google Maps"
                style={{
                  padding: isMobile ? '12px' : `${theme.spacing.sm} ${theme.spacing.md}`,
                  border: `2px solid ${mapsUrlIsValid ? theme.colors.border : theme.colors.warning}`,
                  borderRadius: theme.borderRadius.input,
                  fontSize: isMobile ? '15px' : theme.fonts.sizeBody,
                  fontFamily: theme.fonts.body,
                  backgroundColor: theme.colors.background,
                  color: theme.colors.text,
                  transition: 'all 0.2s ease',
                  width: '100%'
                }}
              />
              {!mapsUrlIsValid && (
                <small
                  data-testid="create-match-maps-url-warning"
                  style={{
                    color: theme.colors.warning,
                    fontSize: isMobile ? '12px' : theme.fonts.sizeSmall,
                    fontFamily: theme.fonts.body,
                  }}
                >
                  ⚠️ No parece un link válido — podés agregarlo después
                </small>
              )}
              <small style={{
                color: theme.colors.textSecondary,
                fontSize: isMobile ? '12px' : theme.fonts.sizeSmall,
                fontStyle: 'italic',
                fontFamily: theme.fonts.body
              }}>
                💡 Podés agregarlo después editando el partido
              </small>
            </div>
            
            {/* Dropdown "Asociar a grupo" — visible si el usuario administra al menos un grupo */}
            {adminGroups.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                <label htmlFor="create-match-group" style={{
                  color: theme.colors.text,
                  fontFamily: theme.fonts.body,
                  fontWeight: 600,
                  fontSize: theme.fonts.sizeBody
                }}>Asociar a grupo (opcional):</label>
                <select
                  id="create-match-group"
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(e.target.value)}
                  style={{
                    padding: isMobile ? '12px' : `${theme.spacing.sm} ${theme.spacing.md}`,
                    border: `2px solid ${theme.colors.border}`,
                    borderRadius: theme.borderRadius.input,
                    fontSize: isMobile ? '15px' : theme.fonts.sizeBody,
                    fontFamily: theme.fonts.body,
                    backgroundColor: theme.colors.background,
                    color: theme.colors.text,
                    transition: 'all 0.2s ease',
                    width: '100%'
                  }}
                >
                  <option value="">Sin grupo</option>
                  {adminGroups.map(g => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Recurring toggle — solo visible cuando hay groupId */}
            {groupId && (
              <div style={{ borderTop: `1px solid ${theme.colors.border}`, paddingTop: 16, marginTop: 4 }}>
                <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}>
                  <span style={{ fontWeight: 600, fontSize: 15, color: theme.colors.text }}>🔄 Partido recurrente</span>
                  <input
                    type="checkbox"
                    checked={isRecurring}
                    onChange={(e) => setIsRecurring(e.target.checked)}
                    style={{ width: 18, height: 18, accentColor: theme.colors.primary, cursor: 'pointer' }}
                  />
                </label>

                {isRecurring && (
                  <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {/* Nombre (opcional) */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                      <label style={{ color: theme.colors.text, fontWeight: 600, fontSize: theme.fonts.sizeBody, fontFamily: theme.fonts.body }}>
                        Nombre (opcional)
                      </label>
                      <input
                        type="text"
                        value={recurringName}
                        onChange={(e) => setRecurringName(e.target.value)}
                        placeholder="Ej: Martes Fulbito"
                        style={{ padding: isMobile ? '12px' : `${theme.spacing.sm} ${theme.spacing.md}`, border: `2px solid ${theme.colors.border}`, borderRadius: theme.borderRadius.input, fontSize: theme.fonts.sizeBody, fontFamily: theme.fonts.body, backgroundColor: theme.colors.background, color: theme.colors.text, width: '100%' }}
                      />
                    </div>

                    {/* Día de la semana */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                      <label style={{ color: theme.colors.text, fontWeight: 600, fontSize: theme.fonts.sizeBody, fontFamily: theme.fonts.body }}>
                        Día de la semana
                      </label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'].map((day, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setRecurringDayOfWeek(i)}
                            style={{
                              padding: '7px 14px',
                              borderRadius: 20,
                              border: `1.5px solid ${recurringDayOfWeek === i ? theme.colors.primary : theme.colors.border}`,
                              background: recurringDayOfWeek === i ? theme.colors.primary : 'transparent',
                              color: recurringDayOfWeek === i ? theme.colors.background : theme.colors.text,
                              fontSize: 13,
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            {day}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Frecuencia */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                      <label style={{ color: theme.colors.text, fontWeight: 600, fontSize: theme.fonts.sizeBody, fontFamily: theme.fonts.body }}>
                        Frecuencia
                      </label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {([
                          { key: 'WEEKLY', label: 'Semanal' },
                          { key: 'BIWEEKLY', label: 'Cada 2 sem.' },
                          { key: 'TRIWEEKLY', label: 'Cada 3 sem.' },
                          { key: 'MONTHLY', label: 'Mensual' },
                        ] as const).map(({ key, label }) => (
                          <button
                            key={key}
                            type="button"
                            onClick={() => setRecurringFrequency(key)}
                            style={{
                              padding: '7px 14px',
                              borderRadius: 20,
                              border: `1.5px solid ${recurringFrequency === key ? theme.colors.primary : theme.colors.border}`,
                              background: recurringFrequency === key ? theme.colors.primary : 'transparent',
                              color: recurringFrequency === key ? theme.colors.background : theme.colors.text,
                              fontSize: 13,
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!isRecurring && (
            <div style={{ display: isMobile ? 'flex' : 'grid', flexDirection: isMobile ? 'column' : undefined, gridTemplateColumns: isMobile ? undefined : '1fr 1fr', gap: isMobile ? '12px' : theme.spacing.md }}>
              <TimeInput
                value={formData.time}
                onChange={(time) => setFormData({...formData, time})}
                label="Horario"
                required
              />

              <DateInput
                value={formData.date}
                onChange={(date) => setFormData({...formData, date})}
                label="Fecha"
                required
              />
            </div>
            )}

            {isRecurring && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                <TimeInput
                  value={formData.time}
                  onChange={(time) => setFormData({...formData, time})}
                  label="Horario"
                  required
                />
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
              <label htmlFor="create-match-price" style={{
                color: theme.colors.text,
                fontFamily: theme.fonts.body,
                fontWeight: 600,
                fontSize: theme.fonts.sizeBody
              }}>Precio de la cancha:</label>
              <input
                id="create-match-price"
                type="number"
                min="0"
                max="9999999"
                step="1"
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: e.target.value})}
                placeholder="Ej: 10000"
                required
                style={{
                  padding: isMobile ? '12px' : `${theme.spacing.sm} ${theme.spacing.md}`,
                  border: `2px solid ${theme.colors.border}`,
                  borderRadius: theme.borderRadius.input,
                  fontSize: isMobile ? '15px' : theme.fonts.sizeBody,
                  fontFamily: theme.fonts.body,
                  backgroundColor: theme.colors.background,
                  color: theme.colors.text,
                  transition: 'all 0.2s ease',
                  width: '100%'
                }}
              />
            </div>
            

            
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
              <label htmlFor="create-match-max-players" style={{
                color: theme.colors.text,
                fontFamily: theme.fonts.body,
                fontWeight: 600,
                fontSize: theme.fonts.sizeBody
              }}>Jugadores por equipo:</label>
              <select
                id="create-match-max-players"
                value={formData.maxPlayers}
                onChange={(e) => setFormData({...formData, maxPlayers: parseInt(e.target.value)})}
                style={{
                  padding: isMobile ? '12px' : `${theme.spacing.sm} ${theme.spacing.md}`,
                  border: `2px solid ${theme.colors.border}`,
                  borderRadius: theme.borderRadius.input,
                  fontSize: isMobile ? '15px' : theme.fonts.sizeBody,
                  fontFamily: theme.fonts.body,
                  backgroundColor: theme.colors.background,
                  color: theme.colors.text,
                  transition: 'all 0.2s ease',
                  width: '100%'
                }}
              >
                <option value={5}>5 jugadores por equipo (10 total)</option>
                <option value={6}>6 jugadores por equipo (12 total)</option>
                <option value={7}>7 jugadores por equipo (14 total)</option>
                <option value={8}>8 jugadores por equipo (16 total)</option>
                <option value={9}>9 jugadores por equipo (18 total)</option>
                <option value={10}>10 jugadores por equipo (20 total)</option>
                <option value={11}>11 jugadores por equipo (22 total)</option>
              </select>
            </div>

            {/* Equipos: nombres + colores (opcional, se pueden cambiar después) */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing.sm,
              padding: theme.spacing.md,
              border: `2px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.input,
            }}>
              <span style={{ color: theme.colors.text, fontWeight: 600, fontSize: theme.fonts.sizeBody }}>
                Equipos (opcional)
              </span>
              <div style={{ display: 'flex', gap: theme.spacing.sm, alignItems: 'center' }}>
                <input
                  aria-label="Nombre del equipo 1"
                  value={formData.team1Name}
                  onChange={(e) => setFormData({ ...formData, team1Name: e.target.value.slice(0, 30) })}
                  placeholder="Equipo 1"
                  style={{
                    flex: 1, padding: isMobile ? '10px' : theme.spacing.sm,
                    border: `2px solid ${theme.colors.border}`, borderRadius: theme.borderRadius.input,
                    backgroundColor: theme.colors.background, color: theme.colors.text,
                    fontSize: isMobile ? '15px' : theme.fonts.sizeBody,
                  }}
                />
                <TeamColorPicker
                  variant="inline"
                  selectedColor={formData.team1Color}
                  onColorSelect={(hex) => setFormData({ ...formData, team1Color: hex })}
                  disabledColor={formData.team2Color}
                />
              </div>
              <div style={{ display: 'flex', gap: theme.spacing.sm, alignItems: 'center' }}>
                <input
                  aria-label="Nombre del equipo 2"
                  value={formData.team2Name}
                  onChange={(e) => setFormData({ ...formData, team2Name: e.target.value.slice(0, 30) })}
                  placeholder="Equipo 2"
                  style={{
                    flex: 1, padding: isMobile ? '10px' : theme.spacing.sm,
                    border: `2px solid ${theme.colors.border}`, borderRadius: theme.borderRadius.input,
                    backgroundColor: theme.colors.background, color: theme.colors.text,
                    fontSize: isMobile ? '15px' : theme.fonts.sizeBody,
                  }}
                />
                <TeamColorPicker
                  variant="inline"
                  selectedColor={formData.team2Color}
                  onColorSelect={(hex) => setFormData({ ...formData, team2Color: hex })}
                  disabledColor={formData.team1Color}
                />
              </div>
            </div>

            {publicMatchDiscover && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing.sm,
                padding: theme.spacing.md,
                border: `2px solid ${theme.colors.border}`,
                borderRadius: theme.borderRadius.input,
                backgroundColor: alpha('primary', 0.05),
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing.sm,
                  cursor: 'pointer',
                  color: theme.colors.text,
                  fontFamily: theme.fonts.body,
                  fontWeight: 600,
                  fontSize: theme.fonts.sizeBody,
                }}>
                  <input
                    type="checkbox"
                    checked={isPublicMatch}
                    onChange={(e) => setIsPublicMatch(e.target.checked)}
                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                    data-testid="create-match-is-public-toggle"
                  />
                  Publicar partido para que otros jugadores puedan pedir unirse
                </label>

                {isPublicMatch && (
                  <>
                    <p style={{
                      margin: 0,
                      color: theme.colors.textSecondary,
                      fontSize: '13px',
                      fontFamily: theme.fonts.body,
                    }}>
                      El partido es público: la gente puede solicitar unirse. Revisás las postulaciones desde el menú del partido.
                    </p>

                    <div>
                      <div style={{
                        color: theme.colors.text,
                        fontFamily: theme.fonts.body,
                        fontWeight: 600,
                        fontSize: theme.fonts.sizeBody,
                        marginBottom: theme.spacing.xs,
                      }}>
                        Tipo de partido
                      </div>
                      <div style={{ display: 'flex', gap: theme.spacing.xs, flexWrap: 'wrap' }}>
                        {([
                          { key: 'MIXED' as MatchGenderType, label: 'Mixto' },
                          { key: 'MALE' as MatchGenderType, label: 'Masculino' },
                          { key: 'FEMALE' as MatchGenderType, label: 'Femenino' },
                        ]).map(({ key, label }) => {
                          const active = matchGenderType === key;
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => setMatchGenderType(key)}
                              data-testid={`create-match-gender-${key}`}
                              style={{
                                padding: `${theme.spacing.xs} ${theme.spacing.md}`,
                                borderRadius: 20,
                                border: `2px solid ${active ? theme.colors.primary : theme.colors.border}`,
                                backgroundColor: active ? theme.colors.primary : theme.colors.background,
                                color: active ? theme.colors.white : theme.colors.text,
                                fontFamily: theme.fonts.body,
                                fontSize: '14px',
                                fontWeight: active ? 600 : 400,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                              }}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <div style={{
                        color: theme.colors.textSecondary,
                        fontFamily: theme.fonts.body,
                        fontSize: '13px',
                        marginBottom: theme.spacing.xs,
                      }}>
                        Nivel esperado (1–5, opcional). Dejá vacío para cualquier nivel.
                      </div>
                      <div style={{ display: 'flex', gap: theme.spacing.sm }}>
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="Nivel mín."
                          value={skillLevelMinStr}
                          onChange={(e) => setSkillLevelMinStr(e.target.value.replace(/[^1-5]/g, '').slice(0, 1))}
                          data-testid="create-match-skill-min"
                          style={{
                            flex: 1,
                            padding: isMobile ? '12px' : `${theme.spacing.sm} ${theme.spacing.md}`,
                            border: `2px solid ${theme.colors.border}`,
                            borderRadius: theme.borderRadius.input,
                            fontSize: isMobile ? '15px' : theme.fonts.sizeBody,
                            fontFamily: theme.fonts.body,
                            backgroundColor: theme.colors.background,
                            color: theme.colors.text,
                          }}
                        />
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="Nivel máx."
                          value={skillLevelMaxStr}
                          onChange={(e) => setSkillLevelMaxStr(e.target.value.replace(/[^1-5]/g, '').slice(0, 1))}
                          data-testid="create-match-skill-max"
                          style={{
                            flex: 1,
                            padding: isMobile ? '12px' : `${theme.spacing.sm} ${theme.spacing.md}`,
                            border: `2px solid ${theme.colors.border}`,
                            borderRadius: theme.borderRadius.input,
                            fontSize: isMobile ? '15px' : theme.fonts.sizeBody,
                            fontFamily: theme.fonts.body,
                            backgroundColor: theme.colors.background,
                            color: theme.colors.text,
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                backgroundColor: loading ? theme.colors.secondary : theme.colors.primary,
                color: theme.colors.white,
                border: 'none',
                borderRadius: theme.borderRadius.button,
                fontSize: theme.fonts.sizeBody,
                fontFamily: theme.fonts.body,
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                transition: 'all 0.2s ease',
                marginTop: theme.spacing.md
              }}
            >
              {loading ? '🔄 Creando...' : isRecurring && groupId ? '🔄 Crear Partido Fijo' : '⚽ Crear Partido'}
            </button>
          </form>
        </div>
      </div>
      
      {/* Modal de Ayuda Contextual */}
      <SpecificHelpModal 
        isOpen={showHelp} 
        onClose={() => {
          setShowHelp(false);
        }} 
        context="createMatch"
      />

    </div>
  );
};

export default CreateMatch; 